import * as React from 'react';
import { StyleObject, styled, useStyletron } from '@visualfabriq/vf-ui-kit';
import { More } from './more';

type RenderEmpty = React.ReactNode | (() => React.ReactNode);

export type ListProps<T> = React.PropsWithChildren<{
  items?: readonly T[];
  renderItem?: (item: T, index: number) => React.ReactNode;
  renderEmpty?: RenderEmpty;
  render?: (props: React.HTMLAttributes<HTMLElement> & { children: React.ReactNode }) => React.ReactNode;
  style?: StyleObject;
  className?: string;
}>;

type ListComponent = (<T = React.ReactNode>(props: ListProps<T>) => React.ReactElement | null) & {
  More: typeof More;
};

export const List = function <T = React.ReactNode>(props: ListProps<T>) {
  const { items, renderItem, renderEmpty, render, style = {}, className, children } = props;
  const [css] = useStyletron();

  const listClassName = [className, css(style)].filter(Boolean).join(' ');

  const ItemComponent = render ? ItemDiv : ItemLi;
  const itemRole = render ? 'listitem' : undefined;

  const renderedItems = (items ?? []).map((item, index) => {
    const node = renderItem ? renderItem(item, index) : (item as unknown as React.ReactNode);
    const key = React.isValidElement(node) && node.key != null ? node.key : index;

    return (
      <ItemComponent key={key} role={itemRole}>
        {node}
      </ItemComponent>
    );
  });

  const renderedChildren = React.Children.toArray(children).map((child, index) => (
    <ItemComponent
      key={React.isValidElement(child) && child.key != null ? child.key : `child-${index}`}
      role={itemRole}
    >
      {child}
    </ItemComponent>
  ));

  const isEmpty = renderedItems.length === 0 && renderedChildren.length === 0;
  if (isEmpty) {
    if (!renderEmpty) return null;
    return <>{typeof renderEmpty === 'function' ? renderEmpty() : renderEmpty}</>;
  }

  const listChildren = (
    <>
      {renderedItems}
      {renderedChildren}
    </>
  );

  if (render) {
    return <>{render({ role: 'list', className: listClassName, children: listChildren })}</>;
  }

  return <Root className={listClassName}>{listChildren}</Root>;
} as ListComponent;

List.More = More;

const Root = styled('ul', ({ $theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale200,
}));

const ItemLi = styled('li', () => ({
  minWidth: 0,
}));

const ItemDiv = styled('div', () => ({
  minWidth: 0,
}));
